import { PlatformBrand } from '@/components/passport/PlatformBrand';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { usePassportStore } from '@/stores/passport-store';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { PassportEventSummaryListDto as PassportEventSummaryListDtoType } from '@tunodo/ticketing-shared';
import { PassportEventSummaryListDto } from '@tunodo/ticketing-shared';
import { CalendarDays, CalendarX, WifiOff } from 'lucide-react';

export const Route = createFileRoute('/passport/events')({
  beforeLoad: () => {
    const { user } = usePassportStore.getState();
    if (!user) {
      throw redirect({ to: '/passport' });
    }
  },
  component: PassportEventsPage,
});

function PassportEventsPage() {
  const user = usePassportStore((s) => s.user)!;
  const clear = usePassportStore((s) => s.clear);
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useQuery<PassportEventSummaryListDtoType>({
    queryKey: ['passport', user.userId, 'events'],
    queryFn: () =>
      apiFetch(`/api/passport/${user.userId}/events`, {
        schema: PassportEventSummaryListDto,
      }),
  });

  function handleLogout() {
    clear();
    navigate({ to: '/passport' });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div
        className="flex items-center justify-center px-4 py-2 shrink-0"
        style={{ backgroundColor: 'var(--passport-cover)' }}
      >
        <PlatformBrand variant="dark" />
      </div>
      <header
        className="flex items-center justify-between px-4 py-3 text-white shrink-0"
        style={{ backgroundColor: '#522B9A' }}
      >
        <div>
          <h1 className="text-base font-semibold">Pasaporte CIMO IPS 2026</h1>
          <p className="text-xs opacity-80">{user.email}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:text-white hover:bg-white/20"
          onClick={handleLogout}
        >
          Salir
        </Button>
      </header>

      <main className="flex flex-col gap-4 p-4 max-w-sm mx-auto w-full">
        <h2 className="text-lg font-bold">Mis eventos</h2>

        {isLoading ? (
          <div className="flex flex-col gap-3 max-w-sm mx-auto w-full">
            <Skeleton className="h-32 rounded-xl" />
          </div>
        ) : isError ? (
          <>
            <Alert variant="destructive" className="max-w-sm mx-auto">
              <WifiOff className="h-4 w-4" />
              <AlertTitle>Error al cargar eventos</AlertTitle>
              <AlertDescription>
                No pudimos obtener tus eventos. Verifica tu conexión e intenta de nuevo.
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mx-auto mt-3 flex"
            >
              Reintentar
            </Button>
          </>
        ) : !data?.length ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <CalendarX className="h-12 w-12 text-muted-foreground/40" />
            <p className="font-medium text-muted-foreground">Sin eventos asignados</p>
            <p className="text-sm text-muted-foreground max-w-xs">
              Tu correo está registrado, pero aún no tienes eventos asignados. Contacta a la
              organización.
            </p>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Volver al inicio
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {data.map((event) => {
              const progressPct =
                event.totalActivities > 0
                  ? Math.round((event.unlockedActivities / event.totalActivities) * 100)
                  : 0;

              return (
                <button
                  key={event.id}
                  type="button"
                  className="rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm hover:border-[#522B9A] hover:bg-purple-50 transition-colors"
                  onClick={() =>
                    navigate({ to: '/passport/events/$eventId', params: { eventId: event.id } })
                  }
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-sm leading-snug">{event.title}</p>
                      {event.date && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                          <CalendarDays className="h-3 w-3" />
                          {new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' }).format(
                            new Date(`${event.date.slice(0, 10)}T12:00:00`),
                          )}
                        </span>
                      )}
                    </div>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                        progressPct === 100
                          ? 'bg-green-100 text-green-700'
                          : progressPct > 0
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-500',
                      )}
                    >
                      {progressPct === 100
                        ? '✓ Completado'
                        : progressPct > 0
                          ? 'En progreso'
                          : 'Sin iniciar'}
                    </span>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {event.unlockedActivities} / {event.totalActivities} actividades
                      </span>
                      <span>{progressPct}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progressPct}%`, backgroundColor: '#522B9A' }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
